import { useEffect, useRef } from "react";
import SEO from "@/components/SEO";
import { Link } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import { CheckCircle2, ArrowRight, Shield, Headphones, Wrench, Wifi, Clock, RefreshCw, Zap } from "lucide-react";
import { useTranslation } from "@/i18n/translations";

function AnimatedSection({ children, className = "", delay = 0 }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-60px" });
  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 24 }} animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 24 }} transition={{ duration: 0.6, delay, ease: [0.16, 1, 0.3, 1] }} className={className}>{children}</motion.div>
  );
}

const COMPARISON_KEYS = [
  { featureKey: "features.terminals", ayapayKey: "pricing.offerTitle", othersKey: "30-80 CHF/mois" },
  { featureKey: "pricing.insurance", ayapayKey: "included", othersKey: "5-15 CHF/mois" },
  { featureKey: "pricing.maintenance", ayapayKey: "pricing.replace", othersKey: "5-15 days" },
  { featureKey: "pricing.support247", ayapayKey: "24/7", othersKey: "business hours" },
  { featureKey: "pricing.sim", ayapayKey: "free", othersKey: "extra" },
];

export default function PricingPage() {
  const { t } = useTranslation();
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const INCLUDED = [
    { icon: Shield, title: t("pricing.insurance"), desc: t("pricing.insuranceDesc") },
    { icon: Wrench, title: t("pricing.maintenance"), desc: t("pricing.maintenanceDesc") },
    { icon: Headphones, title: t("pricing.support247"), desc: t("pricing.support247Desc") },
    { icon: Wifi, title: t("pricing.sim"), desc: t("pricing.simDesc") },
    { icon: Clock, title: t("pricing.replace"), desc: t("pricing.replaceDesc") },
    { icon: RefreshCw, title: t("pricing.updates"), desc: t("pricing.updatesDesc") },
  ];

  const COMPARISON = [
    { feature: t("features.terminals"), ayapay: t("pricing.offerTitle"), others: "Location 30-80 CHF/mois" },
    { feature: t("pricing.insurance"), ayapay: t("pricing.includedTag"), others: "5-15 CHF/mois" },
    { feature: t("pricing.maintenance"), ayapay: t("pricing.replace"), others: "5-15 jours" },
    { feature: "Support", ayapay: "24/7 tel + chat", others: "Business hours" },
    { feature: "5G SIM", ayapay: "Swisscom (free)", others: "Extra cost" },
    { feature: "Engagement", ayapay: t("pricing.howStep2"), others: "12-36 mois" },
  ];

  return (
    <div data-testid="pricing-page">
      <SEO page="pricing" />
      <section className="pt-32 pb-16 lg:pt-40 lg:pb-20 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <AnimatedSection>
            <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#00C853]">{t("pricing.tag")}</span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#0A1128] tracking-tight mt-4 leading-[1.1]">
              {t("pricing.title1")}<br /><span className="text-[#00C853]">{t("pricing.title2")}</span>
            </h1>
            <p className="text-base md:text-lg text-[#475569] mt-6 max-w-2xl mx-auto leading-relaxed">{t("pricing.desc")}</p>
          </AnimatedSection>
        </div>
      </section>

      <section className="py-16 lg:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <AnimatedSection className="text-center mb-10">
            <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#00C853]">Tarification</span>
            <h2 className="text-2xl sm:text-3xl font-semibold text-[#0A1128] tracking-tight mt-3">2 modeles, 1 seul objectif : reduire vos couts</h2>
          </AnimatedSection>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
            {/* IC++ */}
            <AnimatedSection>
              <div data-testid="pricing-icpp" className="relative rounded-2xl border-2 border-[#00C853] bg-white p-8 h-full flex flex-col shadow-lg shadow-[#00C853]/5">
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <span className="bg-[#00C853] text-white text-xs font-bold px-4 py-1 rounded-full">Populaire</span>
                </div>
                <div className="text-center mb-6">
                  <h3 className="text-xl font-bold text-[#1B2B5B]">IC++</h3>
                  <p className="text-sm text-[#475569] mt-1">Interchange Plus Plus</p>
                </div>
                <div className="text-center mb-6">
                  <span className="text-3xl font-bold text-[#00C853]">Tarif sur mesure</span>
                  <div className="text-sm text-[#475569] mt-1">adapte a votre activite</div>
                </div>
                <ul className="space-y-3 flex-1">
                  {[
                    "Interchange (cout reel de la carte)",
                    "Scheme Fees (frais du reseau)",
                    "Merchant Bank Fee",
                    "Transparence totale sur chaque frais",
                    "American Express : 3.5%",
                    "Ideal pour les gros volumes"
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-2.5">
                      <CheckCircle2 size={16} className="text-[#00C853] mt-0.5 shrink-0" />
                      <span className="text-sm text-[#475569]">{item}</span>
                    </li>
                  ))}
                </ul>
                <Link to="/contact" className="mt-8 inline-flex items-center justify-center gap-2 w-full font-semibold bg-[#00C853] text-white rounded-full px-6 py-3 text-sm hover:bg-[#00A844] transition-all">
                  Demander un devis IC++ <ArrowRight size={16} />
                </Link>
              </div>
            </AnimatedSection>

            {/* Full Blend */}
            <AnimatedSection delay={0.15}>
              <div data-testid="pricing-full-blend" className="relative rounded-2xl border border-slate-200 bg-white p-8 h-full flex flex-col">
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <span className="bg-[#1B2B5B] text-white text-xs font-bold px-4 py-1 rounded-full">Simple</span>
                </div>
                <div className="text-center mb-6">
                  <h3 className="text-xl font-bold text-[#1B2B5B]">Full Blend</h3>
                  <p className="text-sm text-[#475569] mt-1">Taux fixe et previsible</p>
                </div>
                <div className="text-center mb-6">
                  <span className="text-5xl font-bold text-[#1B2B5B]">1.4%</span>
                  <div className="text-sm text-[#475569] mt-1">+ 0.09 CHF par transaction</div>
                </div>
                <ul className="space-y-3 flex-1">
                  {[
                    "Taux unique pour toutes les cartes",
                    "Visa, Mastercard, Twint inclus",
                    "American Express : 3.5%",
                    "Pas de frais caches",
                    "Facturation simple et lisible",
                    "Ideal pour les petits et moyens volumes"
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-2.5">
                      <CheckCircle2 size={16} className="text-[#1B2B5B] mt-0.5 shrink-0" />
                      <span className="text-sm text-[#475569]">{item}</span>
                    </li>
                  ))}
                </ul>
                <Link to="/contact" className="mt-8 inline-flex items-center justify-center gap-2 w-full font-semibold border border-[#1B2B5B] text-[#1B2B5B] rounded-full px-6 py-3 text-sm hover:bg-[#1B2B5B] hover:text-white transition-all">
                  Choisir Full Blend <ArrowRight size={16} />
                </Link>
              </div>
            </AnimatedSection>
          </div>

          {/* Terminal gratuit + frais d'activation */}
          <AnimatedSection delay={0.2}>
            <div className="mt-8 bg-[#1B2B5B] rounded-2xl p-6 md:p-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
                <div className="text-center md:text-left md:border-r md:border-white/10 md:pr-6">
                  <div className="flex items-center justify-center md:justify-start gap-3 mb-2">
                    <span className="text-3xl md:text-4xl font-bold text-[#00C853]">CHF 0</span>
                    <span className="text-base md:text-lg text-white font-medium">{t("pricing.forTerminal")}</span>
                  </div>
                  <p className="text-sm text-slate-300">
                    Assurance, maintenance, support 24/7, SIM 5G Swisscom et remplacement sous 24h inclus.
                  </p>
                </div>
                <div className="text-center md:text-left md:pl-2">
                  <div className="flex items-center justify-center md:justify-start gap-3 mb-2">
                    <span className="text-3xl md:text-4xl font-bold text-white">CHF 199.-</span>
                    <span className="text-xs md:text-sm text-[#00C853] font-semibold uppercase tracking-wide bg-[#00C853]/15 border border-[#00C853]/30 rounded-full px-2.5 py-1">Unique</span>
                  </div>
                  <p className="text-sm text-slate-300">
                    Frais d'activation unique : paramétrage du compte marchand, configuration du terminal, formation initiale et activation du support.
                  </p>
                </div>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      <section className="py-24 lg:py-32 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <AnimatedSection className="text-center mb-16">
            <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#00C853]">{t("pricing.includedTag")}</span>
            <h2 className="text-3xl sm:text-4xl font-semibold text-[#0A1128] tracking-tight mt-3">{t("pricing.includedTitle")}</h2>
            <p className="text-base text-[#475569] mt-4 max-w-lg mx-auto">{t("pricing.includedDesc")}</p>
          </AnimatedSection>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {INCLUDED.map((item, i) => (
              <AnimatedSection key={i} delay={i * 0.08}>
                <div className="feature-card bg-white rounded-xl border border-slate-200 p-8 h-full">
                  <div className="w-11 h-11 rounded-lg bg-[#00C853]/10 flex items-center justify-center mb-5">
                    <item.icon size={22} className="text-[#00C853]" />
                  </div>
                  <h3 className="text-lg font-semibold text-[#0A1128] mb-2">{item.title}</h3>
                  <p className="text-sm text-[#475569] leading-relaxed">{item.desc}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 lg:py-32 bg-white">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <AnimatedSection className="text-center mb-12">
            <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#00C853]">{t("pricing.compTag")}</span>
            <h2 className="text-3xl sm:text-4xl font-semibold text-[#0A1128] tracking-tight mt-3">{t("pricing.compTitle")}</h2>
          </AnimatedSection>
          <AnimatedSection>
            <div className="rounded-xl border border-slate-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="comparison-table">
                  <thead>
                    <tr className="bg-[#F8F9FA]">
                      <th className="text-left text-sm font-medium text-[#475569] px-6 py-4 w-1/3"></th>
                      <th className="text-left text-sm font-bold text-[#1B2B5B] px-6 py-4 w-1/3"><span className="flex items-center gap-2"><Zap size={14} className="text-[#00C853]" /> {t("pricing.compAyapay")}</span></th>
                      <th className="text-left text-sm font-medium text-[#475569] px-6 py-4 w-1/3">{t("pricing.compOthers")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {COMPARISON.map((row, i) => (
                      <tr key={i} className="border-t border-slate-100">
                        <td className="px-6 py-4 text-sm font-medium text-[#0A1128]">{row.feature}</td>
                        <td className="px-6 py-4 text-sm text-[#00C853] font-medium">{row.ayapay}</td>
                        <td className="px-6 py-4 text-sm text-[#475569]">{row.others}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      <section className="py-24 lg:py-32 bg-[#F8F9FA]">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <AnimatedSection>
            <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#00C853]">{t("pricing.howTag")}</span>
            <h2 className="text-3xl sm:text-4xl font-semibold text-[#0A1128] tracking-tight mt-3">{t("pricing.howTitle")}</h2>
            <p className="text-base text-[#475569] mt-4 max-w-xl mx-auto leading-relaxed">{t("pricing.howDesc")}</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mt-12">
              <div className="text-center">
                <div className="text-4xl font-bold text-[#1B2B5B]">1</div>
                <h3 className="text-sm font-semibold text-[#0A1128] mt-3">{t("pricing.howStep1")}</h3>
                <p className="text-xs text-[#475569] mt-1">{t("pricing.howStep1Desc")}</p>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold text-[#1B2B5B]">2</div>
                <h3 className="text-sm font-semibold text-[#0A1128] mt-3">{t("pricing.howStep2")}</h3>
                <p className="text-xs text-[#475569] mt-1">{t("pricing.howStep2Desc")}</p>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold text-[#1B2B5B]">3</div>
                <h3 className="text-sm font-semibold text-[#0A1128] mt-3">{t("pricing.howStep3")}</h3>
                <p className="text-xs text-[#475569] mt-1">{t("pricing.howStep3Desc")}</p>
              </div>
            </div>
            <Link to="/contact" data-testid="pricing-contact-cta" className="inline-flex items-center gap-2 mt-10 font-semibold bg-[#1B2B5B] text-white rounded-full px-8 py-3.5 text-sm hover:bg-[#0f1d3d] transition-all">
              {t("pricing.howCta")} <ArrowRight size={16} />
            </Link>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
}
